import { Callout } from './Callout.js';

/**
 * Loading, and failed-to-load — the two states these screens did not have.
 *
 * WHY IT MATTERS MORE THAN IT LOOKS. A tab that renders `data?.map(…)` shows an
 * empty list while the request is in flight AND an empty list when the request
 * failed, and several of these tabs pair that with an empty-state line like
 * "Nothing recorded yet". So a producer whose network dropped was told, in
 * plain English and with confidence, that their production has no activity, no
 * files and no requests. Silence is the one answer a data screen must never
 * give: it is indistinguishable from a true empty, and a true empty is a fact
 * about their production.
 *
 * Rendered ABOVE the list rather than instead of it, so a background refetch
 * that fails does not blank out data the producer is still reading.
 *
 * Returns nothing once the data is there, so a call site is one line.
 */
export function QueryState({
  query,
  subject,
  testId,
}: {
  /** Structural, so it fits any `useQuery` result without importing its generics. */
  query: {
    isPending: boolean;
    isError: boolean;
    error?: unknown;
    refetch?: () => unknown;
    fetchStatus?: string;
  };
  /** What is being loaded, lower case, as it would appear mid-sentence. */
  subject: string;
  testId: string;
}) {
  if (query.isError) {
    return (
      <div style={{ marginTop: 18 }}>
        <Callout
          tone="danger"
          title={`Could not load ${subject}`}
          testId={`${testId}-error`}
          action={
            query.refetch === undefined ? undefined : (
              <button
                type="button"
                className="ctl"
                data-testid={`${testId}-retry`}
                onClick={() => {
                  query.refetch?.();
                }}
              >
                Try again
              </button>
            )
          }
        >
          {/* The message is not shown. It is a transport failure the producer
              cannot act on, and the honest thing to say is that we do not know
              yet — not a stack-shaped sentence that reads like their fault. */}
          Nothing is lost — this is a problem reaching the server, not with your
          production. Try again in a moment.
        </Callout>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <p
        className="mm"
        style={{ fontSize: 13, marginTop: 18 }}
        data-testid={`${testId}-loading`}
        // Announced, so a screen-reader user is told the wait exists rather
        // than meeting a section that is simply empty.
        role="status"
        aria-live="polite"
      >
        Loading {subject}…
      </p>
    );
  }

  return null;
}
